import type { Shops } from '@/Swagger';
import type {
  OrderCountsRequest,
  OrderStatusWithPaginationRequest,
  OrdersRequestParams,
} from '@/app/orders/common/type';
import type { 상품목록_요청_파라미터_서버_타입 } from '@/app/products/common/type';
import type { BrandsDetailRequestType } from '@/app/settings/upload/brand/Brand';

import type { ClaimCountsRequest } from './claim/return/hooks/useGetClaimsCounts';
import type { SaleItemCountsRequest } from './products/origin-list/hooks/useGetSaleItemCounts';

/**
 * TODO: #2257 쿼리 키 분리하기
 */
export const queryKeys = {
  // uploadingTaskExists
  uploadingTaskExists: (shopID: string) => ['uploadingTaskExists', { shopID: shopID }],

  // taskItems
  taskItems: ['taskItems'],
  /**
   * 특정 샵 작업의 모든 상품 목록 query 변형을 대상으로 하는 prefix를 만든다.
   *
   * 필터·페이지네이션 인자가 붙은 query도 함께 무효화해야 생성·적용 후 화면이 서로 다른 캐시를 보여주지 않는다.
   * 공통 `taskItems`, 샵 ID, 작업 ID까지만 포함한 query key를 반환한다.
   *
   * @param shopID 대상 샵 식별자
   * @param taskID 대상 작업 식별자
   * @returns 해당 작업의 모든 상품 목록 query에 일치하는 prefix key
   */
  taskItemsByShopIDPrefix: (shopID: string, taskID: string): [string, string, string] => [
    ...queryKeys.taskItems,
    shopID,
    taskID,
  ],
  taskItemsByShopID: (
    shopID: string,
    taskID: string,
    query?: Shops.TasksItemsList.RequestQuery,
  ): [string, string, string, Shops.TasksItemsList.RequestQuery | undefined] =>
    [...queryKeys.taskItems, shopID, taskID, query] as const,

  // tasks
  tasks: ['tasks'],
  tasksByShopID: (
    shopID: string | undefined,
    searchParams?: Shops.TasksList2.RequestQuery,
  ): [string, { shopID?: string; searchParams?: Shops.TasksList2.RequestQuery }] => [
    'tasksByShopID',
    { shopID, searchParams },
  ],

  taskByID: (taskID: string) => ['task', { taskID: taskID }],
  /**
   * 특정 샵 작업의 상세 query 변형을 대상으로 하는 prefix를 만든다.
   *
   * 상세 query에 검색 인자가 추가된 경우에도 생성 결과 적용 뒤 같은 작업의 상태를 다시 읽어야 한다.
   * 샵 ID와 작업 ID만 담은 객체 key를 반환해 이후 인자가 붙은 query까지 함께 무효화한다.
   *
   * @param shopID 대상 샵 식별자
   * @param taskID 대상 작업 식별자
   * @returns 해당 작업의 모든 상세 query에 일치하는 prefix key
   */
  taskByTaskIDPrefix: (
    shopID: string | undefined,
    taskID: string | undefined,
  ): [string, { shopID?: string; taskID?: string }] => ['taskByID', { shopID, taskID }],
  taskByTaskID: (
    shopID: string | undefined,
    taskID: string | undefined,
    searchParams?: Shops.TasksDetail.RequestQuery,
  ): [string, { shopID?: string; taskID?: string; searchParams?: Shops.TasksDetail.RequestQuery }] => [
    'taskByID',
    { shopID, taskID, searchParams },
  ],

  // shops
  shops: ['shops'],
  shop: ['shop'],
  shopByID: (shopID: string | undefined) =>
    ['shop', { shopID: shopID }] satisfies [string, { readonly shopID?: string }],
  shopAdditionalFieldsByID: (shopID: string) => ['shopAdditionalFields', { shopID: shopID }],

  // mapping-groups
  mappingProperties: ['mapping-properties'] as const,
  mappingGroupByShopID: (shopID: string): [string, { shopID: string }] => ['mapping-group', { shopID }],
  mappingGroupByID: (
    shopID?: string,
    mappingGroupID?: string,
  ): [string, { shopID: string | undefined; mappingGroupID: string | undefined }] => [
    'mappingGroup',
    { shopID, mappingGroupID },
  ],
  mappingGroupsMappingsByID: (
    shopID: string,
    mappingGroupID: string,
  ): [string, { shopID: string; mappingGroupID: string }] => ['mapping-groups-mappings', { shopID, mappingGroupID }],

  // brands
  brands: ['brands'],
  brandsByShopID: (
    shopID: string | undefined,
    groupID?: string,
    searchParams?: Partial<BrandsDetailRequestType>,
  ): [string, { shopID: string | undefined; groupID?: string; searchParams?: Partial<BrandsDetailRequestType> }] => [
    'brands',
    { shopID, groupID, searchParams },
  ],
  brandExcel: ['brandExcel'],
  bookmarkBrands: ['bookmarkBrands'],
  brandGroups: ['brandGroup'],
  brandGroupByID: (
    shopID?: string,
    brandGroupID?: string,
  ): [string, { shopID: string | undefined; brandGroupID: string | undefined }] => [
    'brandGroup',
    { shopID, brandGroupID },
  ],

  // business
  business: ['business'],

  metadataMap: ['metadataMap'],

  // sales
  saleItems: ['saleItems'],
  saleItemsByShopID: (
    shopID: string | undefined,
    filterRequest?: 상품목록_요청_파라미터_서버_타입,
  ): [string, { shopID: string | undefined; filterRequest?: 상품목록_요청_파라미터_서버_타입 }] =>
    ['saleItems', { shopID, ...filterRequest }] as const,
  saleFieldsByShopID: (shopID: string | undefined): [string, { shopID: string | undefined }] => [
    'saleFields',
    { shopID },
  ],
  platformPendingModifyingReasonsBySaleItemID: (
    shopID: string | undefined,
    saleItemID: string | undefined,
  ): [string, { shopID: string | undefined; saleItemID: string | undefined }] => ['saleFields', { shopID, saleItemID }],
  countPendingModifyingByShopID: (shopID: string | undefined): [string, { shopID: string | undefined }] => [
    'countPendingModifying',
    { shopID },
  ],
  saleDashboardsByShopID: (shopID: string | undefined): [string, { shopID?: string }] => ['saleDashboards', { shopID }],

  // platforms
  platformCategoryListByShopID: (
    shopID: string | undefined,
    platformType: string,
  ): [string, { shopID: string | undefined; platformType: string }] => [
    'platformCategoryList',
    { shopID, platformType },
  ],
  platformShippingCompaniesByPlatformType: (platformType: string): [string, { platformType: string }] => [
    'platformShippingCompanies',
    { platformType },
  ],
  platformCancelReasonsByPlatformType: (platformType: string): [string, { platformType: string }] => [
    'platformCancelReasons',
    { platformType },
  ],
  platformReturnReasonsByPlatformType: (platformType: string): [string, { platformType: string }] => [
    'platformReturnReasons',
    { platformType },
  ],
  platformBanksByPlatformType: (platformType: string): [string, { platformType: string }] => [
    'platformBanks',
    { platformType },
  ],
  kakaopayCancelReasons: ['kakaopayCancelReasons'],
  naverpayCancelReasons: ['naverpayCancelReasons'],
  aclosetRejectPriceOfferReasons: ['aclosetRejectPriceOfferReasons'],

  // orders
  orders: ['orders'],
  ordersByShopID: (
    shopID: string,
    orderStatusWithPagination: OrderStatusWithPaginationRequest,
  ): [string, { shopID: string; orderStatusWithPagination: OrderStatusWithPaginationRequest }] => [
    'orders',
    { shopID, orderStatusWithPagination },
  ],
  ordersByShopIDV2: (
    shopID: string,
    searchParams: OrdersRequestParams,
  ): [string, { shopID: string; searchParams: OrdersRequestParams }] => ['orders', { shopID, searchParams }],
  orderCountsByShopID: (
    shopID: string,
    request: OrderCountsRequest,
  ): [string, string, { shopID: string; request: OrderCountsRequest }] => [
    'orders',
    'orderCounts',
    { shopID, request },
  ],
  orderDashboardsByShopID: (shopID: string | undefined): [string, { shopID?: string }] => [
    'orderDashboards',
    { shopID },
  ],

  claimCountsByShopID: (
    shopID: string,
    request: ClaimCountsRequest,
  ): [string, string, { shopID: string; request: ClaimCountsRequest }] => [
    'claims',
    'claimCounts',
    { shopID, request },
  ],
  saleItemCountsByShopID: (
    shopID: string,
    request: SaleItemCountsRequest,
  ): [string, string, { shopID: string; request: SaleItemCountsRequest }] => [
    'saleItems',
    'saleItemCounts',
    { shopID, request },
  ],

  cards: ['cards'],
  plans: ['plans'],
  realtimeSettlements: ['realtimeSettlements'],
  settlements: ['settlements'],
  payments: ['payments'],

  paymentStatusByShopID: (shopID: string): [string, { shopID: string }] => ['payment-status', { shopID }],

  //price-rules
  priceRules: ['priceRules'],
  priceBrandGroups: ['priceBrandGroups'],

  // fabric-price-calcuplators
  fabricPriceCalculators: ['fabricPriceCalculators'],

  //supplier
  supplier: ['supplier'],
  supplierByShopID: (shopID: string) => ['supplier', { shopID }],

  supplierInfo: ['supplierInfo'],

  sizeMappingGroups: ['sizeMappingGroups'],
  sizeMappingGroupsByShopID: (shopID: string) => ['sizeMappingGroups', { shopID }],

  // maintenance
  maintenance: ['maintenance'],

  // notice
  notice: ['notice'],

  // version
  version: ['version'],
} as const;
